import React from 'react';
import { Link } from 'react-router-dom';
import PageSeo from '../components/PageSeo';
import { pocketBillingAiPrivacySeo } from '../seo/pocketBillingAiSeo';

export default function PocketBillingAIPrivacyPolicy() {
  return (
    <div className="min-h-screen bg-stone-50 text-stone-900">
      <PageSeo {...pocketBillingAiPrivacySeo} />
      <header className="border-b border-stone-200 bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between gap-3">
          <Link to="/pocketbillingai" className="text-sm text-indigo-600 hover:text-indigo-800 font-medium">
            ← Back to PocketBillingAI
          </Link>
          <img src="/pocketbillingai-logo.png" alt="Pocket Billing AI" className="h-10 w-10 object-contain" />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-10 sm:py-14">
        <header className="mb-10">
          <p className="text-indigo-600 text-sm font-semibold uppercase tracking-wide mb-2">
            PocketBillingAI
          </p>
          <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 mb-2">Privacy Policy</h1>
          <p className="text-stone-600">Offline Billing software for Shops — by RestroAI</p>
          <p className="text-stone-400 text-sm mt-2">Last updated: June 2026</p>
        </header>

        <div className="space-y-8 text-stone-700 leading-relaxed text-sm sm:text-base">
          <section>
            <h2 className="text-lg font-semibold text-stone-900 mb-2">1. Introduction</h2>
            <p>
              PocketBillingAI (&quot;Service&quot;) is operated by RestroAI (&quot;we&quot;, &quot;us&quot;). This
              Privacy Policy explains how we handle information when you use the PocketBillingAI desktop and Android
              apps, our marketing website, subscription onboarding, and optional cloud sync features. By using the
              Service, you agree to this policy.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-stone-900 mb-2">2. Your shop data stays on your device</h2>
            <p className="mb-3">
              PocketBillingAI is designed <strong>offline-first</strong>. Your daily business data — products, bills,
              stock, udhar accounts, GST records, and settings — is stored <strong>locally</strong> in a database on
              your PC or Android phone. We do <strong>not</strong> host your transaction database on RestroAI servers
              for normal billing operations.
            </p>
            <p>
              You are responsible for device security, backups (including optional Google Drive sync), and protecting
              access with staff controls such as the optional security PIN.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-stone-900 mb-2">3. Information we collect</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Account &amp; subscription:</strong> Shop name, owner name, phone number, email, business type,
                and subscription status when you sign up or renew through RestroAI.
              </li>
              <li>
                <strong>Lead &amp; contact forms:</strong> Details you submit on our website to request a free trial or
                support.
              </li>
              <li>
                <strong>Google account (optional):</strong> If you enable Google Drive sync, you sign in with Google so
                the app can read/write a backup file in your Google Drive. We do not receive your Google password.
              </li>
              <li>
                <strong>Shared bill links (optional):</strong> If you share a bill via a public view link, a copy of that
                bill snapshot may be stored on RestroAI servers only for the purpose of displaying the link to your
                customer.
              </li>
              <li>
                <strong>Technical data:</strong> App version, device type, and basic usage logs if you contact support or
                if error reporting is enabled — to fix bugs and improve the Service.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-stone-900 mb-2">4. Google Drive sync</h2>
            <p>
              When you choose to sync, PocketBillingAI stores an encrypted backup file (e.g.{' '}
              <code className="text-xs bg-stone-100 px-1 rounded">restro-lite-sync.json</code>) in{' '}
              <strong>your</strong> Google Drive account. Only you (and people with access to that Google account)
              control that file. Sync is optional; billing works fully without it.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-stone-900 mb-2">5. How we use information</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>Activate and manage your PocketBillingAI subscription.</li>
              <li>Onboard your shop, provide support, and respond to enquiries.</li>
              <li>Send service updates, renewal reminders, and (with consent) product news.</li>
              <li>Improve the Service, fix issues, and ensure security.</li>
              <li>Comply with applicable laws and enforce our terms.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-stone-900 mb-2">6. Sharing with third parties</h2>
            <p>
              We do not sell your personal information. We may share data only with: (1) service providers who help us
              operate subscriptions, email, or hosting (under confidentiality); (2) Google, when you use Google sign-in
              or Drive sync — subject to Google&apos;s policies; (3) WhatsApp or other apps when{' '}
              <strong>you</strong> choose to share bills; (4) authorities when required by law.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-stone-900 mb-2">7. Customer &amp; GST data</h2>
            <p>
              You may store customer names, mobile numbers, GSTIN, and invoice details in your local database. You are
              the data controller for your customers&apos; information and must collect and use it in compliance with
              applicable privacy and tax laws. GST reports generated by the app are aids for your records; you remain
              responsible for accurate filings.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-stone-900 mb-2">8. Security &amp; retention</h2>
            <p>
              We use reasonable measures to protect subscription and support data. Local shop data security depends on
              your device, PIN settings, and backup practices. We retain subscription and contact records while your
              account is active and as required by law. You may request deletion of account data held by RestroAI by
              contacting us; local data on your device is deleted when you uninstall the app or remove the database file.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-stone-900 mb-2">9. Your rights</h2>
            <p>
              Depending on your location, you may request access, correction, or deletion of personal information we
              hold about you as shop owner or subscriber. Contact us using the details below. You can disable Google
              sync and stop using shared bill links at any time.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-stone-900 mb-2">10. Changes</h2>
            <p>
              We may update this Privacy Policy from time to time. The &quot;Last updated&quot; date will change, and
              material updates may be communicated via email or in-app notice. Continued use after changes constitutes
              acceptance.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-stone-900 mb-2">11. Contact</h2>
            <p>Questions about this Privacy Policy or PocketBillingAI data practices:</p>
            <ul className="list-none mt-2 space-y-1">
              <li>
                <strong>Website:</strong>{' '}
                <Link to="/pocketbillingai" className="text-indigo-600 hover:underline">
                  www.restroai.in/pocketbillingai
                </Link>
              </li>
              <li>
                <strong>Contact:</strong>{' '}
                <Link to="/contact-us" className="text-indigo-600 hover:underline">
                  RestroAI Contact Us
                </Link>
              </li>
            </ul>
          </section>
        </div>

        <footer className="mt-12 pt-8 border-t border-stone-200 text-stone-500 text-sm text-center">
          <p>© {new Date().getFullYear()} RestroAI · PocketBillingAI</p>
          <p className="mt-2 flex flex-wrap justify-center gap-x-3 gap-y-1">
            <Link to="/pocketbillingai" className="text-indigo-600 hover:underline">
              Home
            </Link>
            <Link to="/pocketbillingai/terms" className="text-indigo-600 hover:underline">
              Terms &amp; Conditions
            </Link>
            <Link to="/contact-us" className="text-indigo-600 hover:underline">
              Contact
            </Link>
          </p>
        </footer>
      </main>
    </div>
  );
}
